// useDiscoverFeed - REST initial-load + per-trade WS merge for the
// Discover top-tokens table (REALTIME-CONSISTENCY).
//
// Trade events patch the visible row immediately: price, mcap, liquidity,
// 24h counters and sparkline move without a REST round-trip. Structural
// events merge incrementally — NEVER a refetch per WS message (the old
// refetch-per-message rewound live rows with lagging QuestDB aggregates,
// the visible "grows → snaps back → grows" sawtooth):
//   • trade            → dedup + ordering gate, patch row in place;
//   • token.metadata   → patch identity in place when the row is visible,
//                        else a debounced reload (the token may have just
//                        become eligible for the board);
//   • token.discovered → debounced reload (only a reload can insert a row);
//   • New (by age, no filter): an off-board trade of a token the server
//     already said is not on the board (requested_trade_disposition
//     "excluded") never reloads again — a token only ages, it can never enter
//     a newest-first board later. A new token enters by its first trade
//     ("included"), token.discovered or token.metadata, none of which this
//     skips (audit fe-hooks-01 F6: one /v1/tokens per market trade);
//   • reconnect        → one REST gap-catch after subscriptions are ACKed.
// Every REST snapshot is reconciled through reconcileTokenSnapshot so a
// response computed before the freshest trades became queryable cannot
// rewind live values (see lib/liveFeedGate.ts).

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import type { BoardFilterParams } from '../api/tokens'
import { type TokensSort, type TokensWindow, fetchTokens } from '../api/tokens'
import type { ListEnvelope, TokenSummary, TradeSummary } from '../api/types'
import { rankDiscoverCandidateRows } from '../lib/discoverRank'
import { isMainTokenRow, isNonTonQuotedTrade, rowKeyOf, tradeRowKey } from '../lib/discoverRowKey'
import { HydrationTradeBuffer } from '../lib/hydrationTradeBuffer'
import { LiveTradeGate, patchTokenMetadata, reconcileTokenSnapshot } from '../lib/liveFeedGate'
import {
  applyTradeToTokenSummariesNoReorder,
  applyValuationPatchToTokenSummaries,
  tradeMatchesPrimaryTokenRoute,
} from '../lib/liveTradeRows'
import { TETHER_USDT_MASTER } from '../lib/searchDefault'
import { waitForSubscriptionBarrier } from '../lib/subscriptionBarrier'
import { applyWindowToToken } from '../lib/surfaceTrades'
import { mergeTokenBirth, rememberEarliestTokenBirth } from '../lib/tokenAge'
import { type HeadReloadState, headReloadDue, nextWindowHeadHorizon } from '../lib/windowHead'
import { useWsOptional } from '../ws/WsContext'
import { useNowSec } from './useNow'
import { useWsReseed } from './useWsReseed'
import { useWsSubscription } from './useWsSubscription'

const ADDRESS_PREFIX_RE = /^EQ[A-Za-z0-9_-]{4,}/

interface DiscoverRequest {
  key: string
  controller: AbortController
}

interface DiscoverSnapshot {
  items: TokenSummary[]
}

interface PreparedDiscoverWindow {
  key: string
  tokens: TokenSummary[]
  liquidityFloorApplied: boolean
  gate: LiveTradeGate
  ready: boolean
}

interface DiscoverFeedState {
  key: string
  filtersMatched?: number
  tokens: TokenSummary[]
  hydrated: boolean
  loading: boolean
  error: Error | null
  liquidityFloorApplied: boolean
}

export interface UseDiscoverFeedResult {
  /** Full locally-active candidate membership before ranking and limit. */
  activeTokens: TokenSummary[]
  /** Re-projects a retained immutable event tape at the current local clock tick. */
  projectToken: (token: TokenSummary) => TokenSummary
  tokens: TokenSummary[]
  loading: boolean
  error: Error | null
  refresh: () => void
  /** Rows matching the active filters, before any cut. Undefined with no
   *  filter on — there is nothing to report then. */
  filtersMatched?: number
}

interface TokenMetadataWs {
  jetton_master: string
  ticker?: string
  name?: string
  image_url?: string
}

export interface TokenValuationWs {
  jetton_master: string
  mcap_usd?: number | null
  fdv_usd?: number | null
  liquidity_usd?: number | null
}

interface LaunchpadBirthWs {
  jetton_master: string
  deploy_ts?: string
}

type DiscoverWsPayload =
  | TokenSummary
  | TradeSummary
  | TokenMetadataWs
  | TokenValuationWs
  | LaunchpadBirthWs

/** Bound on the remembered aged-out rows of one New board (cleared when full). */
const AGED_OUT_CAP = 20_000

const NO_TOKENS: TokenSummary[] = []

export function useDiscoverFeed(
  limit = 50,
  sort: TokensSort = 'volume_24h',
  window: TokensWindow = '24h',
  filters?: BoardFilterParams
): UseDiscoverFeedResult {
  // A filter narrows the universe, so it is part of feed OWNERSHIP: a response
  // for one filter must never be merged into the board of another. Serialized
  // into the key for exactly that reason.
  const filterKey = filters ? JSON.stringify(filters) : ''
  const nowSec = useNowSec()
  const ws = useWsOptional()
  const key = discoverFeedKey(limit, sort, window, filterKey)
  const seedSort = discoverSeedSort(sort)
  const gateRef = useRef<{ key: string; gate: LiveTradeGate } | null>(null)
  if (gateRef.current == null || gateRef.current.key !== key) {
    gateRef.current = { key, gate: new LiveTradeGate() }
  }
  const gate = gateRef.current.gate
  const requestRef = useRef<DiscoverRequest | null>(null)
  const fullReloadQueuedRef = useRef(false)
  const queuedTradesRef = useRef(new Map<string, TradeSummary>())
  const coverageRetryRef = useRef(new Set<string>())
  const pendingCoverageTradeRef = useRef<TradeSummary | undefined>(undefined)
  // New by age with no filter: rows the server excluded on their own trade.
  const ageBoard = seedSort === 'new' && filterKey === ''
  const agedOutRef = useRef(new Set<string>())
  const hydrationTradesRef = useRef(new Map<string, HydrationTradeBuffer>())
  const hydrationValuationsRef = useRef(new Map<string, Map<string, TokenValuationWs>>())
  const rememberedBirthsRef = useRef(new Map<string, string>())
  // The default ranked board is 1h. Its first coherent frame must not wait for
  // the much larger 24h tape, but a later 1h -> 24h click should not pay that
  // transfer on the interaction path either. Seed one additional authoritative
  // 24h ownership period after the visible 1h frame commits, then keep it on
  // the same live event stream. This is neither TTL/SWR nor stale-first: an
  // unknown structural event invalidates it, while every covered trade patches
  // the retained exact tape before the window can be consumed.
  const prepared24hRef = useRef<PreparedDiscoverWindow | null>(null)
  const prepared24hRequestRef = useRef<AbortController | null>(null)
  const prepared24hTradesRef = useRef(new HydrationTradeBuffer())
  const reconcilePrepared24hRef = useRef<() => void>(() => {})
  const consumedPreparedKeyRef = useRef<string | null>(null)
  const currentKeyRef = useRef(key)
  const loadRef = useRef<(background?: boolean, afterTrade?: TradeSummary) => void>(() => {})
  currentKeyRef.current = key
  const [state, setState] = useState<DiscoverFeedState>(() => {
    const cached = readDiscoverSnapshot(key)
    return {
      key,
      tokens: cached?.items ?? [],
      hydrated: cached != null,
      loading: cached == null,
      error: null,
      liquidityFloorApplied: false,
    }
  })
  const tokensRef = useRef(state.tokens)
  tokensRef.current = state.tokens
  const hydrated = state.key === key && state.hydrated

  useLayoutEffect(() => {
    const prepared = prepared24hRef.current
    if (!prepared || !prepared.ready || prepared.key !== key) return
    if (key !== discoverFeedKey(limit, 'top', '24h', filterKey)) return
    prepared24hRef.current = null
    consumedPreparedKeyRef.current = key
    hydrationTradesRef.current.delete(key)
    const tokens = patchRememberedTokenBirths(prepared.tokens, rememberedBirthsRef.current)
    prepared.gate.observeTokenSnapshots(tokens)
    gate.observeTokenSnapshots(tokens)
    setState({
      key,
      tokens,
      hydrated: true,
      loading: false,
      error: null,
      liquidityFloorApplied: prepared.liquidityFloorApplied,
    })
  }, [gate, key, limit, filterKey])

  // One request owns one feed key. Same-key reload signals collapse into one
  // trailing rebase; a key change aborts the old request and its late response
  // is unable to mutate the new key's state.
  const load = useCallback(
    (background = false, afterTrade?: TradeSummary) => {
      const requestKey = key
      const active = requestRef.current
      if (active) {
        if (active.key === requestKey) {
          if (afterTrade) queuedTradesRef.current.set(pendingTradeKey(afterTrade), afterTrade)
          else fullReloadQueuedRef.current = true
          return
        }
        active.controller.abort()
        if (requestRef.current === active) requestRef.current = null
        fullReloadQueuedRef.current = false
        queuedTradesRef.current.clear()
      }

      if (!background) {
        const cached = readDiscoverSnapshot(requestKey)
        setState((prev) => ({
          key: requestKey,
          tokens: cached?.items ?? (prev.key === requestKey && prev.hydrated ? prev.tokens : []),
          hydrated: cached != null || (prev.key === requestKey && prev.hydrated),
          loading: cached == null && !(prev.key === requestKey && prev.hydrated),
          error: null,
          liquidityFloorApplied:
            prev.key === requestKey && prev.hydrated ? prev.liquidityFloorApplied : false,
        }))
      }

      const controller = new AbortController()
      const request: DiscoverRequest = { key: requestKey, controller }
      requestRef.current = request
      const completeWindow = seedSort !== 'new'
      const response = waitForSubscriptionBarrier(ws, ['discover'], controller.signal)
        .then(() =>
          fetchTokens(
            {
              limit,
              sort: seedSort,
              window,
              completeWindow,
              afterTrade,
              tradeDelta: completeWindow && afterTrade != null,
              filters,
            },
            { signal: controller.signal }
          )
        )
        .then((result) => {
          if (
            completeWindow &&
            result.snapshot_kind !== 'trade_delta' &&
            result.candidate_set_complete !== true
          ) {
            throw new Error('Discover rolling-window candidate set is incomplete')
          }
          if (
            afterTrade &&
            result.requested_trade_disposition !== 'included' &&
            result.requested_trade_disposition !== 'excluded'
          ) {
            throw new Error('Discover snapshot did not classify the membership-triggering trade')
          }
          if (
            afterTrade &&
            result.requested_trade_disposition === 'included' &&
            !(result.market_included_pending ?? []).includes(pendingTradeKey(afterTrade))
          ) {
            throw new Error('Discover snapshot did not acknowledge the included membership trade')
          }
          if (
            afterTrade &&
            ageBoard &&
            currentKeyRef.current === requestKey &&
            result.requested_trade_disposition === 'excluded'
          ) {
            const aged = agedOutRef.current
            if (aged.size >= AGED_OUT_CAP) aged.clear()
            aged.add(tradeRowKey(afterTrade))
          }
          if (afterTrade) {
            const coverageKey = pendingTradeKey(afterTrade)
            coverageRetryRef.current.delete(coverageKey)
            if (
              pendingCoverageTradeRef.current &&
              pendingTradeKey(pendingCoverageTradeRef.current) === coverageKey
            ) {
              pendingCoverageTradeRef.current = undefined
            }
          }
          return result
        })
      response
        .then((r) => {
          if (
            controller.signal.aborted ||
            currentKeyRef.current !== requestKey ||
            requestRef.current !== request
          ) {
            return
          }
          const items = patchRememberedTokenBirths(
            r.items.filter(isDiscoverProductToken),
            rememberedBirthsRef.current
          )
          setState((prev) => {
            // The request was authoritative when this update was enqueued.
            // React may run functional updaters after the promise's finally()
            // has already cleared requestRef, so ownership must not be checked
            // against that mutable ref again here.
            if (currentKeyRef.current !== requestKey) return prev
            const base = prev.key === requestKey && prev.hydrated ? prev.tokens : []
            const tokens = reconcileDiscoverResponse(base, items, r, gate)
            // a row the server lists is on the board, whatever it said before;
            // a row that LEFT shrinks the board, so a token just below the cut
            // may now qualify: forget every exclusion (review of #1252, B1)
            const listed = new Set(tokens.map(rowKeyOf))
            if (base.some((t) => !listed.has(rowKeyOf(t)))) agedOutRef.current.clear()
            else for (const k of listed) agedOutRef.current.delete(k)
            // observeTokenSnapshots replaces coverage. Observe the merged
            // universe atomically with this commit, never a one-row delta.
            gate.observeTokenSnapshots(tokens)
            writeDiscoverRestSnapshot(requestKey, tokens)
            return {
              key: requestKey,
              tokens,
              filtersMatched: r.filters_matched,
              hydrated: true,
              loading: false,
              error: null,
              liquidityFloorApplied: r.discover_liquidity_floor_applied === true,
            }
          })
        })
        .catch((e: unknown) => {
          if (
            controller.signal.aborted ||
            currentKeyRef.current !== requestKey ||
            requestRef.current !== request
          ) {
            return
          }
          if (background) {
            // Keep the last good atomic board, but never silently lose the one
            // event that requested new membership. One immediate bounded retry
            // remains causally attached to that event (no timer/polling); a
            // second failure is surfaced and the same barrier is retained for
            // the next structural/reconnect signal.
            if (afterTrade) {
              const coverageKey = pendingTradeKey(afterTrade)
              const firstFailure = !coverageRetryRef.current.has(coverageKey)
              if (firstFailure) {
                coverageRetryRef.current.add(coverageKey)
                // A newer B delta does not contain failed A's row. Keep both
                // event keys and drain them causally.
                queuedTradesRef.current.set(coverageKey, afterTrade)
              }
              if (firstFailure) return
            }
            setState((prev) => ({
              ...prev,
              error: e instanceof Error ? e : new Error(String(e)),
            }))
            return
          }
          const fallback = readDiscoverSnapshot(requestKey)
          if (!fallback) {
            hydrationTradesRef.current.get(requestKey)?.clear()
          }
          setState((prev) => ({
            key: requestKey,
            tokens:
              fallback?.items ?? (prev.key === requestKey && prev.hydrated ? prev.tokens : []),
            hydrated: fallback != null || (prev.key === requestKey && prev.hydrated),
            loading: false,
            error: e instanceof Error ? e : new Error(String(e)),
            liquidityFloorApplied:
              prev.key === requestKey && prev.hydrated ? prev.liquidityFloorApplied : false,
          }))
        })
        .finally(() => {
          if (requestRef.current !== request) return
          requestRef.current = null
          const queued = queuedTradesRef.current.entries().next().value as
            | [string, TradeSummary]
            | undefined
          if (queued) queuedTradesRef.current.delete(queued[0])
          const queuedTrade = queued?.[1]
          const fullReloadQueued = queuedTrade == null && fullReloadQueuedRef.current
          if (fullReloadQueued) fullReloadQueuedRef.current = false
          const reloadQueued = queuedTrade != null || fullReloadQueued
          if (reloadQueued && currentKeyRef.current === requestKey) {
            loadRef.current(true, queuedTrade)
          }
        })
    },
    [key, limit, seedSort, window, gate, ws, filters, ageBoard]
  )
  loadRef.current = load

  // Key ownership cleanup runs before the next key's effects.
  useEffect(() => {
    return () => {
      fullReloadQueuedRef.current = false
      queuedTradesRef.current.clear()
      coverageRetryRef.current.clear()
      agedOutRef.current.clear()
      pendingCoverageTradeRef.current = undefined
      const active = requestRef.current
      if (active?.key === key) {
        active.controller.abort()
        if (requestRef.current === active) requestRef.current = null
      }
      hydrationTradesRef.current.delete(key)
    }
  }, [key])

  // Every key starts a fresh ACK-gated authoritative seed. No previous session
  // snapshot is painted while the new ownership period is unresolved.
  useEffect(() => {
    const cached = readDiscoverSnapshot(key)
    setState((prev) => {
      if (prev.key === key) return prev
      return {
        key,
        tokens: cached?.items ?? [],
        hydrated: cached != null,
        loading: cached == null,
        error: null,
        liquidityFloorApplied: false,
      }
    })
    if (consumedPreparedKeyRef.current === key) {
      consumedPreparedKeyRef.current = null
      return
    }
    if (
      key === discoverFeedKey(limit, 'top', '24h', filterKey) &&
      prepared24hRequestRef.current != null
    ) {
      // The visible key adopts the already ACK-gated request. Starting a
      // second 500KiB seed here would make a fast click slower than waiting for
      // the single ownership period already in flight.
      return
    }
    if (!cached) load()
    else load(true)
  }, [key, load, limit, filterKey])

  useEffect(() => {
    if (!hydrated || seedSort !== 'top' || window !== '1h') return
    const preparedKey = discoverFeedKey(limit, 'top', '24h', filterKey)
    if (prepared24hRef.current?.key === preparedKey || prepared24hRequestRef.current != null) {
      return
    }
    const controller = new AbortController()
    let retryVisibleOnFailure = false
    prepared24hRequestRef.current = controller
    prepared24hTradesRef.current.clear()
    waitForSubscriptionBarrier(ws, ['discover'], controller.signal)
      .then(() =>
        fetchTokens(
          { limit, sort: 'top', window: '24h', completeWindow: true },
          { signal: controller.signal }
        )
      )
      .then((response) => {
        if (controller.signal.aborted) return
        if (response.candidate_set_complete !== true) {
          retryVisibleOnFailure = true
          return
        }
        const seedGate = new LiveTradeGate()
        let prepared: PreparedDiscoverWindow | null = {
          key: preparedKey,
          tokens: patchRememberedTokenBirths(
            response.items.filter(isDiscoverProductToken),
            rememberedBirthsRef.current
          ),
          liquidityFloorApplied: response.discover_liquidity_floor_applied === true,
          gate: seedGate,
          ready: true,
        }
        seedGate.observeTokenSnapshots(prepared.tokens)
        const buffered = prepared24hTradesRef.current.drain()
        for (let index = 0; index < buffered.length; index += 1) {
          const trade = buffered[index]
          if (!trade) continue
          const patched = patchPreparedDiscoverTrade(prepared, trade)
          if (patched) {
            prepared = patched
            continue
          }
          prepared = { ...prepared, ready: false }
          for (const unresolved of buffered.slice(index)) {
            prepared24hTradesRef.current.push(unresolved)
          }
          break
        }
        if (prepared.ready && currentKeyRef.current === preparedKey) {
          hydrationTradesRef.current.delete(preparedKey)
          const currentGate = gateRef.current?.key === preparedKey ? gateRef.current.gate : null
          currentGate?.observeTokenSnapshots(prepared.tokens)
          setState({
            key: preparedKey,
            tokens: prepared.tokens,
            hydrated: true,
            loading: false,
            error: null,
            liquidityFloorApplied: prepared.liquidityFloorApplied,
          })
          return
        }
        prepared24hRef.current = prepared
      })
      .catch(() => {
        // The visible 1h board remains authoritative. A failed speculative
        // ownership period is simply unavailable and can never be rendered.
        if (!controller.signal.aborted) retryVisibleOnFailure = true
      })
      .finally(() => {
        if (prepared24hRequestRef.current === controller) {
          prepared24hRequestRef.current = null
        }
        if (prepared24hRef.current?.ready === false) {
          reconcilePrepared24hRef.current()
        }
        if (
          retryVisibleOnFailure &&
          currentKeyRef.current === preparedKey &&
          requestRef.current == null
        ) {
          loadRef.current()
        }
      })
  }, [hydrated, limit, seedSort, window, ws, filterKey])

  useEffect(() => {
    if (
      key === discoverFeedKey(limit, 'top', '1h', filterKey) ||
      key === discoverFeedKey(limit, 'top', '24h', filterKey)
    ) {
      return
    }
    prepared24hRef.current = null
    prepared24hRequestRef.current?.abort()
    prepared24hRequestRef.current = null
    prepared24hTradesRef.current.clear()
    // limit + filterKey are read through discoverFeedKey above: a stale pair
    // here would compare the visible key against a key built for the PREVIOUS
    // filter and keep a speculative window that no longer belongs to it.
  }, [key, limit, filterKey])

  useEffect(
    () => () => {
      prepared24hRequestRef.current?.abort()
      prepared24hRequestRef.current = null
      prepared24hRef.current = null
      prepared24hTradesRef.current.clear()
    },
    []
  )

  const scheduleStructuralReload = useCallback(
    (afterTrade?: TradeSummary) => {
      if (afterTrade) pendingCoverageTradeRef.current = afterTrade
      if (requestRef.current?.key === key) {
        if (afterTrade) queuedTradesRef.current.set(pendingTradeKey(afterTrade), afterTrade)
        else fullReloadQueuedRef.current = true
        return
      }
      if (currentKeyRef.current === key) load(true, afterTrade)
    },
    [key, load]
  )

  const reconcilePrepared24h = useCallback(() => {
    const base = prepared24hRef.current
    if (!base || base.ready || prepared24hRequestRef.current != null) return
    const pending = prepared24hTradesRef.current.drain()
    const trigger = pending.shift()
    for (const trade of pending) prepared24hTradesRef.current.push(trade)
    if (!trigger) return

    const controller = new AbortController()
    prepared24hRequestRef.current = controller
    waitForSubscriptionBarrier(ws, ['discover'], controller.signal)
      .then(() =>
        fetchTokens(
          {
            limit,
            sort: 'top',
            window: '24h',
            completeWindow: true,
            afterTrade: trigger,
            tradeDelta: true,
          },
          { signal: controller.signal }
        )
      )
      .then((response) => {
        const owned = prepared24hRef.current
        if (controller.signal.aborted || !owned || owned.gate !== base.gate) return
        if (
          response.snapshot_kind !== 'trade_delta' ||
          (response.requested_trade_disposition !== 'included' &&
            response.requested_trade_disposition !== 'excluded')
        ) {
          throw new Error('Prepared Discover delta did not classify its trade')
        }
        if (
          response.requested_trade_disposition === 'included' &&
          !(response.market_included_pending ?? []).includes(pendingTradeKey(trigger))
        ) {
          throw new Error('Prepared Discover delta did not acknowledge its trade')
        }

        let next: PreparedDiscoverWindow = {
          ...owned,
          tokens: patchRememberedTokenBirths(
            reconcileDiscoverResponse(
              owned.tokens,
              response.items.filter(isDiscoverProductToken),
              response,
              owned.gate
            ),
            rememberedBirthsRef.current
          ),
          ready: true,
        }
        owned.gate.observeTokenSnapshots(next.tokens)
        const buffered = prepared24hTradesRef.current.drain()
        for (let index = 0; index < buffered.length; index += 1) {
          const trade = buffered[index]
          if (!trade) continue
          const patched = patchPreparedDiscoverTrade(next, trade)
          if (patched) {
            next = patched
            continue
          }
          next = { ...next, ready: false }
          for (const unresolved of buffered.slice(index)) {
            prepared24hTradesRef.current.push(unresolved)
          }
          break
        }
        prepared24hRef.current = next
        if (next.ready && currentKeyRef.current === next.key) {
          hydrationTradesRef.current.delete(next.key)
          const currentGate = gateRef.current?.key === next.key ? gateRef.current.gate : null
          currentGate?.observeTokenSnapshots(next.tokens)
          setState({
            key: next.key,
            tokens: next.tokens,
            hydrated: true,
            loading: false,
            error: null,
            liquidityFloorApplied: next.liquidityFloorApplied,
          })
        }
      })
      .catch(() => {
        if (controller.signal.aborted) return
        prepared24hRef.current = null
        prepared24hTradesRef.current.clear()
        if (
          currentKeyRef.current === discoverFeedKey(limit, 'top', '24h', filterKey) &&
          requestRef.current == null
        ) {
          loadRef.current()
        }
      })
      .finally(() => {
        if (prepared24hRequestRef.current === controller) {
          prepared24hRequestRef.current = null
        }
        if (prepared24hRef.current?.ready === false) {
          reconcilePrepared24hRef.current()
        }
      })
  }, [limit, ws, filterKey])
  reconcilePrepared24hRef.current = reconcilePrepared24h

  // useWsSubscription installs the handler via a ref so the stable
  // closure inside its useEffect keeps a stable subscription — see
  // useWsSubscription.ts. A new `load` identity here does NOT churn
  // the WS subscription.
  useEffect(() => {
    if (hydrated) gate.observeTokenSnapshots(state.tokens)
  }, [gate, hydrated, state.tokens])

  const applyLiveTrade = useCallback(
    (trade: TradeSummary) => {
      // Canonical Tether is permanently excluded from the ranked Discover tabs.
      // Its high-frequency secondary-pool trades cannot change membership,
      // so issuing an exact projection barrier here only creates a guaranteed
      // timeout for a route the Discover projection intentionally does not own.
      if (isDiscoverTetherExcluded(seedSort, trade.base_token)) return
      // Pools without TON are not in the Discover projection yet (3c-3): a
      // barrier on one would wait for an event the projection never ingests.
      if (isNonTonQuotedTrade(trade)) return
      const rows = tokensRef.current
      const rowKey = tradeRowKey(trade)
      const sameToken = rows.some((token) => rowKeyOf(token) === rowKey)
      const exactRoute = rows.some((token) =>
        tradeMatchesPrimaryTokenRoute(token.jetton_master, token.primary_pool_addr, trade)
      )
      const launchpadRouteTransition = rows.some(
        (token) => token.jetton_master === trade.base_token && token.launchpad_status === 'bonding'
      )
      // Discover is the canonical primary route, exactly like its REST
      // projection. A sibling pool trade must not overwrite price, liquidity,
      // counters or tape for that row.
      if (sameToken && !exactRoute && !launchpadRouteTransition) return
      const admission = gate.admit(trade)
      if (!admission.fresh) {
        // Stage-3 B1: the valued upgrade of an already-applied pending frame.
        if (!admission.patch || !exactRoute) return
        setState((prev) => {
          if (prev.key !== key || !prev.hydrated) return prev
          const tokens = applyValuationPatchToTokenSummaries(prev.tokens, trade, admission.newest, {
            exactPoolLiquidity: true,
          })
          if (tokens === prev.tokens) return prev
          writeDiscoverLiveSnapshot(key, tokens)
          return { ...prev, tokens }
        })
        return
      }
      if (!exactRoute) {
        // Membership is an API reduction of the same tape. An off-board trade
        // requests one coalesced event-driven snapshot so a newly qualifying
        // token can enter immediately; this is not a timer or poll. On New by
        // age, a token the server already excluded can never qualify later.
        if (ageBoard && agedOutRef.current.has(tradeRowKey(trade))) return
        scheduleStructuralReload(trade)
        return
      }
      setState((prev) => {
        if (prev.key !== key || !prev.hydrated) return prev
        const tokens = applyTradeToTokenSummariesNoReorder(prev.tokens, trade, {
          countersOnly: !admission.newest,
          exactPoolLiquidity: true,
        })
        if (tokens === prev.tokens) return prev
        writeDiscoverLiveSnapshot(key, tokens)
        return { ...prev, tokens }
      })
    },
    [gate, key, scheduleStructuralReload, seedSort, ageBoard]
  )

  // REST establishes membership; the live tape establishes everything after
  // its snapshot cut. Drain only after the seed is committed so trades that
  // arrived during hydration are neither dropped nor guessed into an empty
  // board. LiveTradeGate rejects events already covered by the REST cursor.
  useEffect(() => {
    if (!hydrated) return
    const buffer = hydrationTradesRef.current.get(key)
    if (!buffer?.size) return
    hydrationTradesRef.current.delete(key)
    for (const trade of buffer.drain()) applyLiveTrade(trade)
  }, [applyLiveTrade, hydrated, key])

  useEffect(() => {
    if (!hydrated) return
    const buffered = hydrationValuationsRef.current.get(key)
    if (!buffered?.size) return
    hydrationValuationsRef.current.delete(key)
    setState((prev) => {
      if (prev.key !== key || !prev.hydrated) return prev
      let tokens = prev.tokens
      for (const valuation of buffered.values()) tokens = patchTokenValuation(tokens, valuation)
      if (tokens === prev.tokens) return prev
      writeDiscoverLiveSnapshot(key, tokens)
      return { ...prev, tokens }
    })
  }, [hydrated, key])

  useWsSubscription<DiscoverWsPayload>('discover', (msg) => {
    switch (msg.event) {
      case 'trade': {
        const trade = msg.data as TradeSummary
        // The speculative 24h ownership period listens to the socket even
        // while another visible key is hydrating. Otherwise a key transition
        // can create a gap between its ACK barrier and eventual commit.
        if (prepared24hRequestRef.current != null) prepared24hTradesRef.current.push(trade)
        const prepared = prepared24hRef.current
        if (prepared?.ready) {
          const patched = patchPreparedDiscoverTrade(prepared, trade)
          if (patched) {
            prepared24hRef.current = patched
          } else {
            prepared24hRef.current = { ...prepared, ready: false }
            if (prepared24hRequestRef.current == null) prepared24hTradesRef.current.push(trade)
            reconcilePrepared24hRef.current()
          }
        } else if (prepared && prepared24hRequestRef.current == null) {
          prepared24hTradesRef.current.push(trade)
          reconcilePrepared24hRef.current()
        }
        if (!hydrated) {
          let buffer = hydrationTradesRef.current.get(key)
          if (!buffer) {
            buffer = new HydrationTradeBuffer()
            hydrationTradesRef.current.set(key, buffer)
          }
          buffer.push(trade)
          return
        }
        applyLiveTrade(trade)
        return
      }
      case 'token.metadata': {
        const d = msg.data as TokenMetadataWs
        const prepared = prepared24hRef.current
        if (prepared) {
          const hasPreparedRow = prepared.tokens.some(
            (row) => row.jetton_master === d.jetton_master
          )
          if (hasPreparedRow) {
            prepared24hRef.current = {
              ...prepared,
              tokens: patchTokenMetadata(prepared.tokens, d),
            }
          } else {
            prepared24hRequestRef.current?.abort()
            prepared24hRequestRef.current = null
            prepared24hTradesRef.current.clear()
            prepared24hRef.current = null
          }
        } else if (prepared24hRequestRef.current) {
          prepared24hRequestRef.current.abort()
          prepared24hRequestRef.current = null
          prepared24hTradesRef.current.clear()
        }
        // Membership check runs on the rendered state (state updaters are
        // queued in React 18+, so a flag set inside one is not readable here).
        const onBoard = state.tokens.some((r) => r.jetton_master === d.jetton_master)
        if (onBoard) {
          setState((prev) => {
            if (prev.key !== key || !prev.hydrated) return prev
            const tokens = patchTokenMetadata(prev.tokens, d)
            if (tokens === prev.tokens) return prev
            writeDiscoverLiveSnapshot(key, tokens)
            return { ...prev, tokens }
          })
          return
        }
        // Not on the board → resolved metadata may have just made it eligible
        // (the New tab's freshest rows); only a reload can insert it. Its
        // earlier exclusion may have been the metadata gate, not age: its
        // trades reload again until the server says otherwise (review B2).
        agedOutRef.current.delete(d.jetton_master)
        scheduleStructuralReload()
        return
      }
      case 'token.valuation': {
        const valuation = msg.data as TokenValuationWs
        const prepared = prepared24hRef.current
        if (prepared) {
          const tokens = patchTokenValuation(prepared.tokens, valuation)
          if (tokens !== prepared.tokens) prepared24hRef.current = { ...prepared, tokens }
        }
        if (!hydrated) {
          let buffered = hydrationValuationsRef.current.get(key)
          if (!buffered) {
            buffered = new Map()
            hydrationValuationsRef.current.set(key, buffered)
          }
          buffered.set(valuation.jetton_master, valuation)
          return
        }
        setState((prev) => {
          if (prev.key !== key || !prev.hydrated) return prev
          const tokens = patchTokenValuation(prev.tokens, valuation)
          if (tokens === prev.tokens) return prev
          writeDiscoverLiveSnapshot(key, tokens)
          return { ...prev, tokens }
        })
        return
      }
      case 'launchpad.deploy': {
        const birth = msg.data as LaunchpadBirthWs
        const birthTS = rememberEarliestTokenBirth(
          rememberedBirthsRef.current,
          birth.jetton_master,
          birth.deploy_ts
        )
        if (!birthTS) return
        const canonicalBirth = { ...birth, deploy_ts: birthTS }
        const prepared = prepared24hRef.current
        if (prepared) {
          const tokens = patchTokenBirth(prepared.tokens, canonicalBirth)
          if (tokens !== prepared.tokens) prepared24hRef.current = { ...prepared, tokens }
        }
        setState((prev) => {
          if (prev.key !== key || !prev.hydrated) return prev
          const tokens = patchTokenBirth(prev.tokens, canonicalBirth)
          if (tokens === prev.tokens) return prev
          writeDiscoverLiveSnapshot(key, tokens)
          return { ...prev, tokens }
        })
        return
      }
      case 'token.discovered': {
        // A token our catalog has just learned: its earlier exclusion was not
        // age (the catalog refresh behind this event may land after the
        // reload below, so its own trades must reload again).
        const discovered = (msg.data as { jetton_master?: unknown } | null)?.jetton_master
        if (typeof discovered === 'string') agedOutRef.current.delete(discovered)
        prepared24hRef.current = null
        prepared24hRequestRef.current?.abort()
        prepared24hRequestRef.current = null
        prepared24hTradesRef.current.clear()
        scheduleStructuralReload()
        return
      }
      case 'discover.tick':
        // Trades already patch visible rows. A periodic aggregate tick is not
        // a reason to re-read lagging storage and rewind the live board.
        return
      default:
        // Unknown/future events must opt in before they can create network
        // work, and cannot be assumed covered by a speculative ownership
        // period. Fail closed without touching the visible board.
        prepared24hRef.current = null
        prepared24hRequestRef.current?.abort()
        prepared24hRequestRef.current = null
        prepared24hTradesRef.current.clear()
        return
    }
  })

  // Reconnect catch-up is the only background REST rebase. During a healthy
  // socket the board is event-driven; there is no network timer.
  useWsReseed(
    useCallback(() => {
      prepared24hRef.current = null
      prepared24hRequestRef.current?.abort()
      prepared24hRequestRef.current = null
      prepared24hTradesRef.current.clear()
      gate.reset()
      load(true, pendingCoverageTradeRef.current)
    }, [gate, load])
  )

  const visibleCache = state.key === key ? null : readDiscoverSnapshot(key)
  const rawTokens = state.key === key ? state.tokens : (visibleCache?.items ?? [])
  const windowSec = discoverWindowSeconds(window)
  // A capped row's expiry head is exact only through its horizon (lib/
  // windowHead.ts); past it, a background rebase brings the next head (retried
  // with a bounded backoff while the horizon stays the same — lib/windowHead.ts
  // headReloadDue). A data edge the snapshot itself names, not a polling loop.
  // Only this key's own rows: a previous key's cached rows are not reloaded here.
  const ownTokens = state.key === key ? state.tokens : NO_TOKENS
  const headHorizon = useMemo(() => nextWindowHeadHorizon(ownTokens), [ownTokens])
  const headReloadRef = useRef<HeadReloadState | null>(null)
  useEffect(() => {
    const due = headReloadDue(headReloadRef.current, headHorizon, nowSec)
    if (!due) return
    headReloadRef.current = due
    loadRef.current(true)
  }, [headHorizon, nowSec])
  const projectToken = useCallback(
    (token: TokenSummary) => applyWindowToToken(token, nowSec, windowSec),
    [nowSec, windowSec]
  )
  const activeTokens = useMemo(
    () =>
      rawTokens
        .map(projectToken)
        .filter((token) => sort === 'new' || token.window_events == null || token.txns_24h > 0)
        .filter(
          (token) =>
            sort === 'new' ||
            !state.liquidityFloorApplied ||
            (typeof token.liquidity_usd === 'number' && token.liquidity_usd >= 1_000)
        ),
    [rawTokens, projectToken, sort, state.liquidityFloorApplied]
  )
  const tokens = useMemo(
    () => rankDiscoverCandidateRows(activeTokens, sort).slice(0, limit),
    [activeTokens, sort, limit]
  )
  const loading = state.key === key ? state.loading : visibleCache == null
  const error = state.key === key ? state.error : null
  // refresh is arg-shielded: callers pass it straight to onRetry/onClick and a
  // truthy event object must not become load()'s `background` flag.
  const refresh = useCallback(() => load(), [load])
  return {
    activeTokens,
    projectToken,
    tokens,
    loading,
    error,
    refresh,
    filtersMatched: state.key === key ? state.filtersMatched : undefined,
  }
}

function patchPreparedDiscoverTrade(
  prepared: PreparedDiscoverWindow | null,
  trade: TradeSummary
): PreparedDiscoverWindow | null {
  if (!prepared || isDiscoverTetherExcluded('top', trade.base_token)) return prepared
  const rowKey = tradeRowKey(trade)
  const sameToken = prepared.tokens.some((token) => rowKeyOf(token) === rowKey)
  const exactRoute = prepared.tokens.some((token) =>
    tradeMatchesPrimaryTokenRoute(token.jetton_master, token.primary_pool_addr, trade)
  )
  if (sameToken && !exactRoute) {
    const isBondingTransition = prepared.tokens.some(
      (token) => token.jetton_master === trade.base_token && token.launchpad_status === 'bonding'
    )
    return isBondingTransition ? null : prepared
  }
  // A complete prepared universe cannot honestly absorb a trade for a missing
  // master without the event-scoped metadata/membership reduction.
  if (!exactRoute) return null
  const admission = prepared.gate.admit(trade)
  if (!admission.fresh) {
    if (!admission.patch) return prepared
    const tokens = applyValuationPatchToTokenSummaries(prepared.tokens, trade, admission.newest, {
      exactPoolLiquidity: true,
    })
    return tokens === prepared.tokens ? prepared : { ...prepared, tokens }
  }
  const tokens = applyTradeToTokenSummariesNoReorder(prepared.tokens, trade, {
    countersOnly: !admission.newest,
    exactPoolLiquidity: true,
  })
  return tokens === prepared.tokens ? prepared : { ...prepared, tokens }
}

function reconcileTokenDelta(
  current: TokenSummary[],
  patches: TokenSummary[],
  gate: LiveTradeGate
): TokenSummary[] {
  if (patches.length === 0) return current
  const next = [...current]
  const indexByRow = new Map(next.map((row, index) => [rowKeyOf(row), index]))
  for (const patch of patches) {
    const index = indexByRow.get(rowKeyOf(patch))
    if (index == null) {
      indexByRow.set(rowKeyOf(patch), next.length)
      next.push(patch)
      continue
    }
    const existing = next[index]
    if (!existing) {
      next[index] = patch
      continue
    }
    const merged = reconcileTokenSnapshot([existing], [patch], gate)[0]
    next[index] = merged ?? patch
  }
  return next
}

export function patchTokenValuation(
  tokens: TokenSummary[],
  valuation: TokenValuationWs
): TokenSummary[] {
  if (!valuation.jetton_master) return tokens
  let changed = false
  const next = tokens.map((token) => {
    // Liquidity and cap describe the token's main pool, never a pool row.
    if (token.jetton_master !== valuation.jetton_master || !isMainTokenRow(token)) return token
    const mcap = finiteValuation(valuation.mcap_usd) ? valuation.mcap_usd : token.mcap_usd
    const fdv = finiteValuation(valuation.fdv_usd) ? valuation.fdv_usd : token.fdv_usd
    const liquidity = finiteValuation(valuation.liquidity_usd)
      ? valuation.liquidity_usd
      : token.liquidity_usd
    if (mcap === token.mcap_usd && fdv === token.fdv_usd && liquidity === token.liquidity_usd) {
      return token
    }
    changed = true
    return { ...token, mcap_usd: mcap, fdv_usd: fdv, liquidity_usd: liquidity }
  })
  return changed ? next : tokens
}

export function patchTokenBirth(tokens: TokenSummary[], birth: LaunchpadBirthWs): TokenSummary[] {
  if (!birth.jetton_master) return tokens
  let changed = false
  const next = tokens.map((token) => {
    if (token.jetton_master !== birth.jetton_master) return token
    const merged = mergeTokenBirth(token, { birth_ts: birth.deploy_ts })
    if (merged === token) return token
    changed = true
    return merged
  })
  return changed ? next : tokens
}

function patchRememberedTokenBirths(
  tokens: TokenSummary[],
  births: ReadonlyMap<string, string>
): TokenSummary[] {
  let next = tokens
  for (const [jettonMaster, deployTS] of births) {
    next = patchTokenBirth(next, { jetton_master: jettonMaster, deploy_ts: deployTS })
  }
  return next
}

function finiteValuation(value: number | null | undefined): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0
}

function reconcileDiscoverResponse(
  current: TokenSummary[],
  items: TokenSummary[],
  response: ListEnvelope<TokenSummary>,
  gate: LiveTradeGate
): TokenSummary[] {
  if (response.snapshot_kind !== 'trade_delta') {
    return reconcileTokenSnapshot(current, items, gate)
  }
  switch (response.requested_trade_action) {
    case 'upsert':
      return reconcileTokenDelta(current, items, gate)
    case 'remove':
      return current.filter(
        (row) =>
          rowKeyOf(row) !== (response.requested_trade_row_key || response.requested_trade_master)
      )
    default:
      return current
  }
}

function discoverWindowSeconds(window: TokensWindow): number {
  switch (window) {
    case '1m':
      return 60
    case '5m':
      return 300
    case '15m':
      return 900
    case '30m':
      return 1800
    case '1h':
      return 3600
    default:
      return 86_400
  }
}

function discoverFeedKey(
  limit: number,
  sort: TokensSort,
  window: TokensWindow,
  filterKey = ''
): string {
  if (sort === 'top' || sort === 'trending' || sort === 'surge') {
    // These tabs are three pure rankings over one complete immutable window
    // universe. Sort is deliberately not part of ownership: switching axes
    // must not abort/reseed the identical multi-hundred-KiB event tape.
    return withFilter(`ranked:${window}`, filterKey)
  }
  return withFilter(`${limit}:${sort}:${window}`, filterKey)
}

// A filter narrows the universe, so it is part of feed OWNERSHIP: a response
// fetched under one filter must never merge into the board of another. Appended
// here rather than at the call site so every derived key — including the
// speculative 24h one — carries it and cannot drift from the visible key.
function withFilter(base: string, filterKey: string): string {
  return filterKey ? `${base}|${filterKey}` : base
}

function discoverSeedSort(sort: TokensSort): TokensSort {
  return sort === 'trending' || sort === 'surge' ? 'top' : sort
}

function isDiscoverTetherExcluded(sort: TokensSort, master: string | undefined): boolean {
  return (
    master === TETHER_USDT_MASTER &&
    (sort === 'top' || sort === 'trending' || sort === 'surge' || sort === 'new')
  )
}

function pendingTradeKey(trade: Pick<TradeSummary, 'pool_addr' | 'ts' | 'tx_hash'>): string {
  const pool = trade.pool_addr ?? ''
  const nanos = BigInt(Math.trunc(trade.ts)) * 1_000_000_000n
  return `${pool.length}:${pool}${nanos}:${trade.tx_hash.length}:${trade.tx_hash}`
}

function readDiscoverSnapshot(key: string): DiscoverSnapshot | null {
  try {
    window.sessionStorage.removeItem(`wt:discover-feed:v3:${key}`)
  } catch {
    // Storage may be unavailable; it is never an input to rendering.
  }
  return null
}

function writeDiscoverRestSnapshot(_key: string, _items: TokenSummary[]): void {}

function writeDiscoverLiveSnapshot(_key: string, _items: TokenSummary[]): void {}

export function clearDiscoverFeedSnapshotCacheForTest(): void {
  // No-op: product snapshots are intentionally not cached.
}

export function isDiscoverProductToken(t: TokenSummary): boolean {
  if (!t.jetton_master || t.metadata_resolved !== true) {
    return false
  }
  return hasHumanTokenLabel(t.jetton_master, t.ticker, t.name)
}

function hasHumanTokenLabel(master: string, ticker?: string, name?: string): boolean {
  const tt = (ticker ?? '').trim()
  const nn = (name ?? '').trim()
  if (!tt || !nn) return false
  return !isAddressLikeTokenLabel(master, tt) && !isAddressLikeTokenLabel(master, nn)
}

function isAddressLikeTokenLabel(master: string, label: string): boolean {
  const clean = label.trim()
  if (!clean) return false
  if (master.startsWith(clean) && clean.length >= 6) return true
  return ADDRESS_PREFIX_RE.test(clean) && clean.length >= 24
}
